/* i18n/locales.js — THE LANGUAGE REGISTRY.
 *
 * Languages are DATA, never a hardcoded list in a component. This module owns
 * the catalogue of known languages and lets more be added at runtime (the
 * SuperAdmin "add a language" flow calls registerLocale), so shipping a new
 * language never requires a code change — only a message catalogue.
 *
 * `dir` drives the whole layout: registering a locale with dir:"rtl" makes the
 * document flip automatically (see i18n/index.js), which is how Arabic works
 * without any per-component code.
 */

/** The languages that ship with the platform. `enabled` can be flipped per
 *  tenant from SuperAdmin; the list itself is extensible at runtime. */
const BUILT_IN = [
  { code: "en", name: "English", native: "English", dir: "ltr", enabled: true },
  { code: "sq", name: "Albanian", native: "Shqip", dir: "ltr", enabled: true },
  { code: "es", name: "Spanish", native: "Español", dir: "ltr", enabled: true },
  { code: "fr", name: "French", native: "Français", dir: "ltr", enabled: true },
  { code: "it", name: "Italian", native: "Italiano", dir: "ltr", enabled: true },
  { code: "de", name: "German", native: "Deutsch", dir: "ltr", enabled: true },
  { code: "ar", name: "Arabic", native: "العربية", dir: "rtl", enabled: true },
];

const registry = new Map();
BUILT_IN.forEach((l) => registry.set(l.code, { ...l }));

/** Add or update a language at runtime (SuperAdmin / tenant settings). */
export function registerLocale(def) {
  if (!def || !def.code) return null;
  const code = String(def.code).toLowerCase();
  const prev = registry.get(code) || {};
  const next = {
    code,
    name: def.name || prev.name || code,
    native: def.native || prev.native || def.name || code,
    dir: def.dir === "rtl" ? "rtl" : (prev.dir || "ltr"),
    enabled: def.enabled !== undefined ? !!def.enabled : (prev.enabled !== undefined ? prev.enabled : true),
  };
  registry.set(code, next);
  return next;
}
/** Bulk-apply a tenant's language configuration (from the server). */
export function configureLocales(list) {
  (list || []).forEach(registerLocale);
  return listLocales();
}
export function listLocales(opts) {
  const all = Array.from(registry.values());
  return (opts && opts.all) ? all : all.filter((l) => l.enabled);
}
export function getLocale(code) {
  return registry.get(String(code || "").toLowerCase()) || null;
}
export function isRtl(code) {
  const l = getLocale(code);
  return !!l && l.dir === "rtl";
}
export const DEFAULT_LOCALE = "en";
